import { useSubmitFormMutation } from '@/__generated__/types';
import { FORM_TYPE_LABELS, FORM_TYPE_OPTIONS } from '@/constants/form';
import {
    POS_INQUIRY_BUSINESS_TYPES,
    POS_INQUIRY_HEARD_FROM_OPTIONS,
    POS_INQUIRY_INITIAL_VALUES,
    POS_INQUIRY_OTHER_BUSINESS_TYPE
} from '@/constants/posInquiry';
import { Turnstile } from '@marsidev/react-turnstile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import {
    Alert,
    Box,
    Container,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
    TextFieldProps,
    Typography
} from '@mui/material';
import LoadingButton from '@mui/material/Button';
import { Form, FormikProvider, useFormik } from 'formik';
import { MuiTelInput } from 'mui-tel-input';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { posInquiryValidationSchema } from './schema';
import { PosInquiryFormValues } from './types';

const POS_INQUIRY_TURNSTILE_SITE_KEY = import.meta.env.VITE_POS_INQUIRY_CLOUDFLARE_TURNSTILE_KEY;

const fieldProps = (
    formik: ReturnType<typeof useFormik<PosInquiryFormValues>>,
    name: keyof PosInquiryFormValues
): Pick<TextFieldProps, 'name' | 'value' | 'onBlur' | 'error' | 'helperText'> => {
    const touched = Boolean(formik.touched[name]);
    const error = formik.errors[name];

    return {
        name,
        value: formik.values[name],
        onBlur: formik.handleBlur,
        error: touched && Boolean(error),
        helperText: touched && error ? error : undefined
    };
};

const PosInquiry = (): JSX.Element => {
    const { t, i18n } = useTranslation();
    const countryNameLanguage = i18n.language.startsWith('zh') ? 'zh' : 'en';
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [usesOtherBusinessType, setUsesOtherBusinessType] = useState(false);
    const [submitForm] = useSubmitFormMutation();

    const formik = useFormik<PosInquiryFormValues>({
        initialValues: POS_INQUIRY_INITIAL_VALUES,
        validationSchema: posInquiryValidationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitError('');
            if (!POS_INQUIRY_TURNSTILE_SITE_KEY) {
                setSubmitError(t('posInquiry.turnstileKeyError'));
                return;
            }
            if (!turnstileToken) {
                setSubmitError(t('posInquiry.submitError'));
                setSubmitting(false);
                return;
            }
            try {
                const { data } = await submitForm({
                    variables: {
                        input: {
                            formType: values.formType,
                            turnstileToken,
                            fields: [
                                { label: 'restaurantName', value: values.restaurantName.trim() },
                                { label: 'contactName', value: values.contactName.trim() },
                                { label: 'mobileNumber', value: values.mobileNumber.trim() },
                                { label: 'email', value: values.email.trim() },
                                { label: 'city', value: values.city.trim() },
                                { label: 'businessType', value: values.businessType.trim() },
                                { label: 'message', value: values.message.trim() },
                                { label: 'referralSource', value: values.referralSource }
                            ]
                        }
                    }
                });

                if (!data?.submitForm.success) {
                    throw new Error(data?.submitForm.message || 'Failed to submit POS inquiry.');
                }

                setIsSubmitted(true);
            } catch {
                setSubmitError(t('posInquiry.submitError'));
            } finally {
                setSubmitting(false);
            }
        }
    });

    if (isSubmitted) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.neutral} 100%)`,
                    pt: { xs: 12, sm: 16 },
                    pb: { xs: 6, md: 10 }
                }}
            >
                <Container maxWidth="sm">
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 5 },
                            borderRadius: 1,
                            textAlign: 'center',
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            boxShadow: (theme) => theme.customShadows.z16
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 64, mb: 2, color: 'info.main' }} />
                        <Typography variant="h3" sx={{ mb: 2, color: 'text.primary' }}>
                            {t('posInquiry.successTitle')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                            {t('posInquiry.successDescription')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                            {t('posInquiry.successClosing')}
                        </Typography>
                        <Alert
                            severity="info"
                            icon={<PhoneOutlinedIcon />}
                            sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`
                            }}
                        >
                            {t('posInquiry.urgentEnquiries')}
                        </Alert>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.neutral} 100%)`,
                pt: { xs: 11, sm: 15 },
                pb: { xs: 5, md: 8 }
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={{ xs: 3, md: 5 }} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Stack spacing={3} sx={{ position: { md: 'sticky' }, top: { md: 96 } }}>
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: 'info.main',
                                        fontWeight: 800,
                                        letterSpacing: 1.4
                                    }}
                                >
                                    {t('posInquiry.eyebrow')}
                                </Typography>
                                <Typography variant="h2" sx={{ mt: 1, mb: 2, color: 'text.primary' }}>
                                    {t('posInquiry.title')}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                                    {t('posInquiry.description1')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    {t('posInquiry.description2')}
                                </Typography>
                            </Box>

                            <Stack spacing={2}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        boxShadow: (theme) => theme.customShadows.z8
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5}>
                                        <StorefrontOutlinedIcon sx={{ color: 'info.main' }} />
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                                {t('posInquiry.businessCardTitle')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {t('posInquiry.businessCardDescription')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        boxShadow: (theme) => theme.customShadows.z8
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5}>
                                        <LocalOfferOutlinedIcon sx={{ color: 'info.main' }} />
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                                {t('posInquiry.welcomePackCardTitle')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {t('posInquiry.welcomePackCardDescription')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 7 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 4 },
                                borderRadius: 1,
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                boxShadow: (theme) => theme.customShadows.z16
                            }}
                        >
                            <FormikProvider value={formik}>
                                <Form noValidate onSubmit={formik.handleSubmit}>
                                    <Stack spacing={2.5}>
                                        {submitError && <Alert severity="error">{submitError}</Alert>}

                                        <FormControl fullWidth error={Boolean(formik.touched.formType && formik.errors.formType)}>
                                            <FormLabel required sx={{ mb: 1 }}>
                                                {t('posInquiry.formType')}
                                            </FormLabel>
                                            <TextField
                                                select
                                                fullWidth
                                                name="formType"
                                                value={formik.values.formType}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={Boolean(formik.touched.formType && formik.errors.formType)}
                                            >
                                                {FORM_TYPE_OPTIONS.map((formType) => (
                                                    <MenuItem key={formType} value={formType}>
                                                        {t(`form.types.${formType}`, FORM_TYPE_LABELS[formType])}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            {formik.touched.formType && formik.errors.formType && (
                                                <FormHelperText>{formik.errors.formType}</FormHelperText>
                                            )}
                                        </FormControl>

                                        <TextField
                                            fullWidth
                                            required
                                            label={t('posInquiry.restaurantName')}
                                            placeholder={t('posInquiry.restaurantNamePlaceholder')}
                                            onChange={formik.handleChange}
                                            {...fieldProps(formik, 'restaurantName')}
                                        />
                                        <TextField
                                            fullWidth
                                            required
                                            label={t('posInquiry.contactPersonName')}
                                            placeholder={t('posInquiry.contactPersonNamePlaceholder')}
                                            onChange={formik.handleChange}
                                            {...fieldProps(formik, 'contactName')}
                                        />

                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <MuiTelInput
                                                    fullWidth
                                                    required
                                                    label={t('posInquiry.mobileNumber')}
                                                    value={formik.values.mobileNumber}
                                                    onChange={(value, info) => {
                                                        formik.setFieldValue('mobileNumber', value);
                                                        if (info.countryCode) {
                                                            formik.setFieldValue('mobileCountry', info.countryCode);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        formik.setFieldTouched('mobileNumber', true);
                                                    }}
                                                    defaultCountry={formik.values.mobileCountry}
                                                    preferredCountries={[formik.values.mobileCountry]}
                                                    forceCallingCode
                                                    disableFormatting
                                                    langOfCountryName={countryNameLanguage}
                                                    placeholder={t('posInquiry.mobileNumberPlaceholder')}
                                                    error={Boolean(formik.touched.mobileNumber && formik.errors.mobileNumber)}
                                                    helperText={
                                                        formik.touched.mobileNumber && formik.errors.mobileNumber
                                                            ? formik.errors.mobileNumber
                                                            : undefined
                                                    }
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                maxHeight: 360
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label={t('posInquiry.emailAddress')}
                                                    placeholder={t('posInquiry.emailAddressPlaceholder')}
                                                    type="email"
                                                    onChange={formik.handleChange}
                                                    {...fieldProps(formik, 'email')}
                                                />
                                            </Grid>
                                        </Grid>

                                        <TextField
                                            fullWidth
                                            required
                                            label={t('posInquiry.city')}
                                            placeholder={t('posInquiry.cityPlaceholder')}
                                            onChange={formik.handleChange}
                                            {...fieldProps(formik, 'city')}
                                        />

                                        <FormControl fullWidth error={Boolean(formik.touched.businessType && formik.errors.businessType)}>
                                            <FormLabel required sx={{ mb: 1 }}>
                                                {t('posInquiry.businessType')}
                                            </FormLabel>
                                            <TextField
                                                select
                                                fullWidth
                                                name="businessType"
                                                value={usesOtherBusinessType ? POS_INQUIRY_OTHER_BUSINESS_TYPE : formik.values.businessType}
                                                onChange={(event) => {
                                                    const nextBusinessType = event.target.value;
                                                    setUsesOtherBusinessType(nextBusinessType === POS_INQUIRY_OTHER_BUSINESS_TYPE);
                                                    formik.setFieldValue(
                                                        'businessType',
                                                        nextBusinessType === POS_INQUIRY_OTHER_BUSINESS_TYPE ? '' : nextBusinessType
                                                    );
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={Boolean(formik.touched.businessType && formik.errors.businessType)}
                                            >
                                                {POS_INQUIRY_BUSINESS_TYPES.map((businessType) => (
                                                    <MenuItem key={businessType} value={businessType}>
                                                        {t(`posInquiry.businessTypes.${businessType}`)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            {formik.touched.businessType && formik.errors.businessType && (
                                                <FormHelperText>{formik.errors.businessType}</FormHelperText>
                                            )}
                                        </FormControl>

                                        {usesOtherBusinessType && (
                                            <TextField
                                                fullWidth
                                                required
                                                label={t('posInquiry.otherBusinessType')}
                                                placeholder={t('posInquiry.otherBusinessTypePlaceholder')}
                                                onChange={formik.handleChange}
                                                {...fieldProps(formik, 'businessType')}
                                            />
                                        )}

                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            label={t('posInquiry.requirementsOptional')}
                                            placeholder={t('posInquiry.requirementsPlaceholder')}
                                            onChange={formik.handleChange}
                                            {...fieldProps(formik, 'message')}
                                        />

                                        <FormControl
                                            fullWidth
                                            error={Boolean(formik.touched.referralSource && formik.errors.referralSource)}
                                        >
                                            <FormLabel required sx={{ mb: 1 }}>
                                                {t('posInquiry.heardFrom')}
                                            </FormLabel>
                                            <TextField
                                                select
                                                fullWidth
                                                name="referralSource"
                                                value={formik.values.referralSource}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={Boolean(formik.touched.referralSource && formik.errors.referralSource)}
                                            >
                                                {POS_INQUIRY_HEARD_FROM_OPTIONS.map((heardFrom) => (
                                                    <MenuItem key={heardFrom} value={heardFrom}>
                                                        {t(`posInquiry.heardFromOptions.${heardFrom}`)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            {formik.touched.referralSource && formik.errors.referralSource && (
                                                <FormHelperText>{formik.errors.referralSource}</FormHelperText>
                                            )}
                                        </FormControl>

                                        {POS_INQUIRY_TURNSTILE_SITE_KEY && (
                                            <Turnstile
                                                siteKey={POS_INQUIRY_TURNSTILE_SITE_KEY}
                                                onSuccess={setTurnstileToken}
                                                onExpire={() => setTurnstileToken(null)}
                                                onError={() => setTurnstileToken(null)}
                                            />
                                        )}

                                        <LoadingButton
                                            fullWidth
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                            loading={formik.isSubmitting}
                                            disabled={Boolean(POS_INQUIRY_TURNSTILE_SITE_KEY) && !turnstileToken}
                                            sx={{
                                                height: 48,
                                                fontWeight: 700
                                            }}
                                        >
                                            {t('posInquiry.submit')}
                                        </LoadingButton>
                                    </Stack>
                                </Form>
                            </FormikProvider>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default PosInquiry;
